import React from 'react';
import { useSelector } from 'react-redux';
import { Button, Carousel, CarouselItem } from '@americanexpress/dls-react';
import { Helmet } from 'react-helmet';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { useRouteParams } from '@/context/routeParamsContext';
import { getLocaleData } from '@/selectors/localeSelectors';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { formatText } from '@/helpers/utils';
import LFLink from '../common/LFLink';
import AirportsSearchBox from '../common/AirportsSearchBox';
import LandingPartnerLogosSection from './LandingPartnerLogosSection';
import styles from './LandingPage.module.scss';
import CardCalloutNotification from '../common/CardCalloutNotification';
import ChevronLeft from '../icons/ChevronLeft';

const LandingPage = () => {
  const cmsImgBaseUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const { product } = useRouteParams();
  const { locale } = useSelector(getLocaleData);
  const {
    pageTitles: { landing },
    featuredAirports,
    carousel,
    productDisplayNames,
  } = useCommonContent();
  const isUsMarket = !locale || locale.toLocaleLowerCase() === 'en-us';

  // for meta description
  const productName = productDisplayNames[product]?.displayName?.replace(
    'The ',
    ''
  );

  return (
    <div className="dls-white-bg">
      <Helmet>
        <title>{landing?.title}</title>
        <meta
          name="description"
          content={formatText(landing?.description, { productName })}
        />
      </Helmet>

      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
        <div className={styles.heroGrayBackground} />
        <Carousel isNavigationOnSlides={true} className={styles.heroCarousel}>
          {(product === 'delta-reserve' ? carousel.slice(1) : carousel).map(
            (item) => (
              <CarouselItem
                id={item.title}
                key={item.title}
                className={styles.heroCarousel}
              >
                <div
                  className={styles.heroImageContainer}
                  style={{
                    backgroundImage: `url(${cmsImgBaseUrl}${item.img})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div className={styles.heroImageGradient} />
                  <div className={styles.heroImageContent}>
                    <h1 className={styles.imageTitle}>{item.title}</h1>
                    <p className={styles.imageDescription}>
                      {item.description}
                    </p>
                    {item.url ? (
                      <a href={item.url} target="_blank" rel="noreferrer">
                        <Button>{item.cta}</Button>
                      </a>
                    ) : (
                      <Button
                        onClick={() => {
                          const searchBar =
                            document.querySelector('#findLounges');
                          if (searchBar) {
                            searchBar.focus();
                          }
                        }}
                      >
                        {item.cta}
                      </Button>
                    )}
                  </div>
                </div>
              </CarouselItem>
            )
          )}
        </Carousel>
        <div className={styles.searchAndCalloutWrapper}>
          <div>
            <AirportsSearchBox className={styles.searchWrapper} />
            <CardCalloutNotification className={styles.cardCalloutLanding} />
          </div>
        </div>
      </div>
      <div className={styles.partnerAndTermsWrapper}>
        <h3 className="tw-heading-md-regular tw-mt-10 tw-text-gray-900">
          {featuredAirports?.header}
        </h3>
        <ul className="tw-my-6 tw-grid tw-gap-6 sm_tw-grid-cols-2 lg_tw-grid-cols-3">
          {featuredAirports?.airports.map((airport) => {
            const urlAirportIata = airport.iata ? `/${airport.iata}` : '';
            return (
              <li key={airport.iata}>
                <LFLink
                  key={`featured-airports-link-${airport.iata}`}
                  element="featured-airports-link"
                  className="tw-flex"
                  to={`${product}${urlAirportIata}`}
                >
                  <img
                    alt=""
                    src={`${cmsImgBaseUrl}${airport.image}`}
                    className="tw-h-12 tw-w-12 tw-rounded-lg tw-bg-gray-300"
                  />
                  <div className="tw-ml-4 tw-flex tw-flex-col">
                    <span className="tw-text-gray-800">{airport.name}</span>
                    <span className="tw-text-xs tw-text-gray-700">
                      {airport.iata} • {airport.city}
                    </span>
                  </div>
                </LFLink>
              </li>
            );
          })}
        </ul>
        {isUsMarket && (
          <LFLink
            to={`${product}/all-airports`}
            className="tw-my-2dot5 tw-inline-flex tw-items-center tw-font-medium tw-text-link"
            element="all-airports-link"
          >
            {featuredAirports?.link}
            <ChevronLeft className="tw-ml-1 tw-inline tw-h-3 tw-w-3 tw-rotate-180" />
          </LFLink>
        )}
        <LandingPartnerLogosSection />
      </div>
    </div>
  );
};

export default LandingPage;
